import { useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom/server";
import { Datepicker as VanillaDatepicker } from "vanillajs-datepicker";
import "vanillajs-datepicker/css/datepicker.css";
import "../../../static/css/datepicker.css";
import Button from "./Button.jsx";
import Icon from "./Icon.jsx";
import th from "vanillajs-datepicker/locales/th";

import { getDoctorUnavailableDate } from "../api/services/clinic/appointment";
import { THAI_MONTH_NAMES, ENGLISH_MONTH_NAMES } from "../utils/date.js";


const BUDDHIST_ERA_THRESHOLD = 2400;

type CalendarEra = "AD" | "BE";
type DateValue = string | Date | null | undefined;
type DatepickerInputChange =
    | Date
    | {
        target: {
            name: string;
            value: string;
        };
    };

interface DatepickerCommonProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
    onChange?: (value: DatepickerInputChange) => void;
}

interface DatepickerProps {
    commonProps?: DatepickerCommonProps;
    value?: DateValue;
    defaultViewDate?: Date;
    minDate?: string | Date;
    maxDate?: string | Date;
    inputClasses?: string;
    disabled?: boolean;
    inline?: boolean;
    headless?: boolean;
    id?: string;
    onViewChange?: (date: Date) => void;
    selectedMonth?: number;
    selectedYear?: number;
    doctorId?: string | number;
    patientId?: string | number;
    clinicBranchId?: string | number;
    onClickNextMonthDate?: (month: number) => void;
    calendarEra?: CalendarEra;
}

interface DatepickerInstance {
    element: HTMLElement;
    picker?: {
        element?: HTMLElement;
    };
    destroy: () => void;
    setDate: (date: Date, options?: { render?: boolean }) => void;
    setOptions: (options: { datesDisabled?: Date[] }) => void;
}

interface DatepickerChangeDateEvent extends Event {
    detail: {
        date?: Date;
    };
}

interface DatepickerChangeViewEvent extends Event {
    detail: {
        viewDate?: string | Date | number;
    };
}

interface DatepickerChangeMonthEvent extends Event {
    detail: {
        date: Date;
    };
}

type DatepickerObserver = {
    disconnect: () => void;
};

export function parseThaiDisplayString(str: unknown): Date | null {
    if (!str || typeof str !== "string") {
        return null;
    };

    if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const dt = new Date(str);
        if (!isNaN(dt.getTime())) {
            return dt;
        }
    }

    const m = str.trim().match(/^(\d{1,2})\s+(.+?)\s+(\d{4})$/);
    if (!m) return null;
    const day = parseInt(m[1], 10);
    let monthIdx = THAI_MONTH_NAMES.indexOf(m[2]);
    if (monthIdx === -1) {
        monthIdx = ENGLISH_MONTH_NAMES.indexOf(m[2]);
    }

    let year = parseInt(m[3], 10);
    if (year >= BUDDHIST_ERA_THRESHOLD) {
        year -= 543
    };

    const dt = new Date(year, monthIdx, day);
    return isNaN(dt.getTime()) ? null : dt;
}

function buddhistEraYearsViewCells(
    dp: DatepickerInstance | null,
    calendarEra: CalendarEra,
): DatepickerObserver {
    if (!dp || !dp.picker || !dp.picker.element) return { disconnect: () => { } };
    const pickerEl = dp.picker.element;

    const toBE = (y: number) => (calendarEra === "AD" ? y : (y < BUDDHIST_ERA_THRESHOLD ? y + 543 : y));

    const convertViews = () => {
        const yearsView = pickerEl.querySelector('.datepicker-view.years');
        const decadesView = pickerEl.querySelector('.datepicker-view.decades');
        const view = yearsView || decadesView;

        if (!view) return;

        view.querySelectorAll('.datepicker-cell').forEach((cell) => {
            const txt = (cell.textContent || '').trim();
            if (/^\d{4}$/.test(txt)) {
                const y = parseInt(txt, 10);
                if (y < BUDDHIST_ERA_THRESHOLD) {
                    cell.textContent = String(toBE(y));
                }
            }
        });
    };

    const schedule = () => setTimeout(convertViews, 0);

    const observer = new MutationObserver(schedule);
    observer.observe(pickerEl, { childList: true, subtree: true });

    dp.element.addEventListener('show', schedule);
    dp.element.addEventListener('changeView', schedule);

    const handleClick = (e: MouseEvent) => {
        const target = e.target instanceof Element ? e.target : null;
        if (target?.closest('.view-switch, .prev-button, .next-button')) {
            schedule();
        }
    };
    pickerEl.addEventListener('click', handleClick);

    schedule();

    return {
        disconnect: () => {
            observer.disconnect();
            if (dp.element) {
                dp.element.removeEventListener('show', schedule);
                dp.element.removeEventListener('changeView', schedule);
            }
            pickerEl.removeEventListener('click', handleClick);
        }
    };
}

function trimDayViewHeader(
    dp: DatepickerInstance | null,
    calendarEra: CalendarEra,
): DatepickerObserver {
    if (!dp || !dp.picker || !dp.picker.element) return { disconnect: () => { } };
    const pickerEl = dp.picker.element;
    const viewSwitch = pickerEl.querySelector('.view-switch');
    if (!viewSwitch) return { disconnect: () => { } };

    const toBE = (y: number) => (calendarEra === "AD" ? y : (y < BUDDHIST_ERA_THRESHOLD ? y + 543 : y));

    const apply = () => {
        const text = (viewSwitch.textContent || '').trim();

        if (/^\D.+\s+\d{4}$/.test(text)) {
            const monthOnly = text.replace(/\s+\d{4}$/, '');
            if (viewSwitch.textContent !== monthOnly) {
                viewSwitch.textContent = monthOnly;
            }
            return;
        }

        if (/^\d{4}$/.test(text)) {
            const y = parseInt(text, 10);
            if (y < BUDDHIST_ERA_THRESHOLD) viewSwitch.textContent = String(toBE(y));
            return;
        }

        if (/^\d{4}-\d{4}$/.test(text)) {
            const [start, end] = text.split('-').map((n) => parseInt(n, 10));
            if (start < BUDDHIST_ERA_THRESHOLD && end < BUDDHIST_ERA_THRESHOLD) {
                viewSwitch.textContent = `${toBE(start)}-${toBE(end)}`;
            }
            return;
        }
    };

    const observer = new MutationObserver(apply);
    observer.observe(viewSwitch, { childList: true, characterData: true, subtree: true });
    apply();

    return observer;
}

/** 
 * 
 * Datepicker component
 * A custom datepicker component that supports buddhist eras
 * 
 * @param {Object} props
 * @param {Object} props.commonProps
 *   Common props to be spread to the input element
 * @param {string|Date} props.value
 *   The current value of the datepicker, in display string or Date object
 * @param {Date} [props.defaultViewDate]
 *   The date to set the calendar view to, without changing the selected date
 * @param {string|Date} [props.minDate]
 *   The minimum selectable date, in ISO string or Date object
 * @param {string|Date} [props.maxDate]
 *   The maximum selectable date, in ISO string or Date object
 * @param {string} props.inputClasses
 *   Additional classes for the input element
 * @param {boolean} [props.disabled=false]
 *   Whether the datepicker is disabled
 * @param {boolean} [props.inline=false]
 *   Whether to render the datepicker inline
 * @param {boolean} [props.headless=false]
 *   Whether to hide the datepicker controls
 * @param {string} [props.id]
 *   The id attribute for the datepicker element
 * @param {function} [props.onViewChange]
 *  A callback function that is called when the calendar view changes
 */

export default function Datepicker({
    commonProps = {},
    value,
    defaultViewDate,
    minDate,
    maxDate,
    inputClasses,
    disabled,
    inline = false,
    headless = false,
    id,
    onViewChange,
    selectedMonth,
    selectedYear,
    doctorId,
    patientId,
    clinicBranchId,
    onClickNextMonthDate,
    calendarEra = "BE",
}: DatepickerProps) {
    const calendarRef = useRef<HTMLInputElement | HTMLDivElement | null>(null);
    const datepickerRef = useRef<DatepickerInstance | null>(null);

    const [disabledDate, setDisabledDate] = useState<Date[] | null>(null);

    useEffect(() => {
        if (!(patientId && clinicBranchId && selectedMonth && selectedYear)) return;
        const getDisabledDate = async () => {
            // Helper to build a "YYYY-MM" string for a given year+month offset
            const toParam = (y: number, m: number) => {
                const d = new Date(y, m - 1, 1); // handles month over/underflow automatically
                const yr = d.getFullYear();
                const mo = (d.getMonth() + 1).toString().padStart(2, '0');
                return `${yr}-${mo}`;
            };

            const prevParam = toParam(selectedYear, selectedMonth - 1);
            const currParam = toParam(selectedYear, selectedMonth);
            const nextParam = toParam(selectedYear, selectedMonth + 1);

            // Fetch all three months in parallel
            const [prevData, currData, nextData] = await Promise.all([
                getDoctorUnavailableDate(currParam, doctorId ?? "", patientId, clinicBranchId),
                getDoctorUnavailableDate(nextParam, doctorId ?? "", patientId, clinicBranchId),
                getDoctorUnavailableDate(prevParam, doctorId ?? "", patientId, clinicBranchId),
            ]);

            const dateList = [
                ...(prevData.unavailable_dates ?? []),
                ...(currData.unavailable_dates ?? []),
                ...(nextData.unavailable_dates ?? []),
            ].map((dateString) => new Date(dateString));



            setDisabledDate(dateList);
        };
        getDisabledDate();
    }, [doctorId, patientId, clinicBranchId, selectedMonth, selectedYear]);

    useEffect(() => {
        const calendarElement = calendarRef.current;

        if (calendarElement) {
            if (datepickerRef.current) {
                datepickerRef.current.destroy();
                datepickerRef.current = null;
            }

            if (VanillaDatepicker?.locales && !VanillaDatepicker.locales.th) {
                VanillaDatepicker.locales.th = th?.th || th;
            }

            const formatter = {
                toDisplay: (date: Date) => {
                    const locale = calendarEra === "AD" ? "en-US" : "th-TH";
                    return date.toLocaleDateString(locale, {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                    });
                },
                toValue: (str: string) => {
                    return parseThaiDisplayString(str);
                },
            };

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const defaultMinDate = new Date(today);
            defaultMinDate.setFullYear(defaultMinDate.getFullYear() - 150);

            const defaultMaxDate = new Date(today);
            defaultMaxDate.setFullYear(defaultMaxDate.getFullYear() + 150);
            const options = {
                autohide: !inline,
                format: formatter,
                minDate: minDate instanceof Date ? minDate : (typeof (minDate) === "string" ? new Date(minDate) : defaultMinDate),
                maxDate: maxDate instanceof Date ? maxDate : (typeof (maxDate) === "string" ? new Date(maxDate) : defaultMaxDate),
                defaultViewDate: defaultViewDate,
                todayBtn: false,
                clearBtn: false,
                language: calendarEra === "AD" ? 'en' : 'th',
                todayHighlight: true,
                orientation: 'bottom',
                prevArrow: ReactDOM.renderToStaticMarkup(
                    <Button hierarchy="secondary" size="md" icon="chevron-left" />
                ),
                nextArrow: ReactDOM.renderToStaticMarkup(
                    <Button hierarchy="secondary" size="md" icon="chevron-right" />
                ),
                inline: inline,
            };

            datepickerRef.current = new VanillaDatepicker(calendarElement, options) as DatepickerInstance;
            if (disabledDate !== null) {
                datepickerRef.current.setOptions({
                    datesDisabled: disabledDate
                });
            }

            const initial = parseThaiDisplayString(value || "");

            if (initial) {
                datepickerRef.current.setDate(initial, { render: true });
            } else if (value instanceof Date) {
                datepickerRef.current.setDate(value, { render: true });
            }

            const headerObserver = trimDayViewHeader(datepickerRef.current, calendarEra);
            const yearsObserver = buddhistEraYearsViewCells(datepickerRef.current, calendarEra);



            const onChangeDate = (ev: Event) => {
                const detail = (ev as DatepickerChangeDateEvent).detail;
                const pickedDate = detail.date;
                if (!pickedDate) return;

                if (typeof commonProps.onChange === "function") {
                    if (inline) {
                        commonProps.onChange(pickedDate);
                    } else {
                        const displayVal = formatter.toDisplay(pickedDate);
                        const syntheticEvent = {
                            target: {
                                name: calendarElement instanceof HTMLInputElement ? calendarElement.name : "",
                                value: displayVal,
                            },
                        };
                        commonProps.onChange(syntheticEvent);
                    }
                }
            };

            calendarElement.addEventListener("changeDate", onChangeDate);

            const onChangeView = (ev: Event) => {
                const detail = (ev as DatepickerChangeViewEvent).detail;
                if (onViewChange && detail.viewDate) {
                    onViewChange(new Date(detail.viewDate));
                }
            };
            calendarElement.addEventListener("changeView", onChangeView);

            const removeControls = () => {
                if (headless) {
                    const controls = calendarElement.querySelector(".datepicker-controls");
                    if (controls) controls.remove();
                }
            };
            removeControls();
            calendarElement.addEventListener("render", removeControls);

            const onMonthChange = (e: Event) => {
                if (!onClickNextMonthDate) return;
                const newDate = (e as DatepickerChangeMonthEvent).detail.date;
                const newMonth = newDate.getMonth() + 1;
                onClickNextMonthDate(newMonth)
            }

            calendarElement.addEventListener('changeMonth', onMonthChange);

            return () => {
                calendarElement.removeEventListener("changeDate", onChangeDate);
                calendarElement.removeEventListener("changeView", onChangeView);
                calendarElement.removeEventListener("render", removeControls);
                calendarElement.removeEventListener("changeMonth", onMonthChange);
                if (headerObserver) headerObserver.disconnect();
                if (yearsObserver) yearsObserver.disconnect();
                if (datepickerRef.current) {
                    datepickerRef.current.destroy();
                    datepickerRef.current = null;
                }
            };
        }
        // remove value, onchange, onViewChange
    }, [minDate, maxDate, value, commonProps.onChange, inline, headless, onViewChange, disabledDate]);



    if (inline) {
        return <div ref={(element) => { calendarRef.current = element; }} id={id}></div>;
    }

    return (
        <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                <Icon
                    name="calendar"
                    size="md"
                    className={
                        disabled
                            ? "text-creation-neutral-container-on-surface-disabled"
                            : "text-creation-neutral-container-on-surface-default"
                    }
                />
            </div>
            <input
                {...commonProps}
                ref={(element) => { calendarRef.current = element; }}
                type="text"
                className={`${inputClasses} ps-7 appearance-none`}
                value={value instanceof Date ? String(value) : value || ""}
                readOnly
            />
        </div >
    );
}
